import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';

import { fg } from '@confluence/feature-gating';
import {
	type ContentExtensionsCount,
	retrieveContentExtensionsCountFromNode,
} from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers';

export type AdfStats = {
	contentExtensionsCount: ContentExtensionsCount;
	nodeCountByType: Record<string, number>;
	totalNodeCount: number;
};

type CustomPerfData = { contentExtensionsCount?: ContentExtensionsCount } | null | undefined;

export function useAdfStats(adfDocument: DocNode, customPerfData?: CustomPerfData): AdfStats {
	const shouldRetrieveContentExtensionsCount =
		Boolean(customPerfData) && fg('cc_complexit_fe_reduce_adf_parse');

	const adfStats = useMemo(() => {
		const contentExtensionsCount: ContentExtensionsCount = {
			connectExtensions: {},
			forgeExtensions: {},
		};
		const nodeCountByType: Record<string, number> = {};
		let totalNodeCount = 0;

		traverse(adfDocument, {
			any: (node, parent) => {
				nodeCountByType[node.type] = (nodeCountByType[node.type] || 0) + 1;
				totalNodeCount++;

				// This is the alternative to `retrieveContentExtensionsCount` which avoids one more parsing of the ADF string into JSON.
				if (
					shouldRetrieveContentExtensionsCount &&
					// Note: Like `retrieveContentExtensionsCount`, this only considers the top-level content of the document!
					parent?.node?.type === 'doc'
				) {
					retrieveContentExtensionsCountFromNode(node, contentExtensionsCount);
				}
			},
		});

		return {
			contentExtensionsCount,
			nodeCountByType,
			totalNodeCount,
		};
	}, [adfDocument, shouldRetrieveContentExtensionsCount]);

	if (shouldRetrieveContentExtensionsCount) {
		customPerfData!.contentExtensionsCount = adfStats.contentExtensionsCount;
	}

	return adfStats;
}
